"use client"
import { useState, useEffect, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Badge,
  Spinner,
  Alert,
  Input,
  Button,
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
} from "reactstrap"
import DataTable, { type TableColumn } from "react-data-table-component"
import { FaArrowLeft, FaPlus, FaTrash, FaSearch, FaSync } from "react-icons/fa"
import { RoleMenuService, MenuService } from "../../../Service/role-management-service"
import type { Menu, RoleMenu } from "../rolemanagement/types"
import { toast } from "react-toastify"

interface RoleMenuManagementProps {
  role: {
    id: number
    name: string
  } | null
}

export default function RoleMenuManagement({ role }: RoleMenuManagementProps) {
  const navigate = useNavigate()

  // State management
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [assignedMenus, setAssignedMenus] = useState<RoleMenu[]>([])
  const [allMenus, setAllMenus] = useState<Menu[]>([])
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [selectedMenuIds, setSelectedMenuIds] = useState<number[]>([])
  const [menuSearchTerm, setMenuSearchTerm] = useState("")

  // Pagination state for assigned menus table
  const [currentPage, setCurrentPage] = useState(1)
  const [perPage, setPerPage] = useState(10)
  const [totalRows, setTotalRows] = useState(0)

  // Fetch all available menus (unpaginated) for assignment
  const fetchAllMenus = useCallback(async () => {
    try {
      console.log("Fetching all menus for assignment...")
      const menusResponse = await MenuService.getAllUnpaginated()

      console.log("All menus response:", menusResponse)
      setAllMenus(menusResponse.results || [])
    } catch (error: any) {
      console.error("fetchAllMenus error:", error)
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to load available menus"
      toast.error(errorMessage)
      setAllMenus([])
    }
  }, [])

  // Fetch assigned menus for the role (paginated)
  const fetchAssignedMenus = useCallback(async () => {
    if (!role?.id) return

    try {
      console.log("Fetching assigned menus for role:", role.id)
      const roleMenusResponse = await RoleMenuService.getByRole(role.id, {
        page: currentPage,
        limit: perPage,
      })

      console.log("Assigned menus response:", roleMenusResponse)
      setAssignedMenus(roleMenusResponse.results || [])
      setTotalRows(roleMenusResponse.count || 0)
    } catch (error: any) {
      console.error("fetchAssignedMenus error:", error)
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to load assigned menus"
      toast.error(errorMessage)
      setAssignedMenus([])
      setTotalRows(0)
    }
  }, [role, currentPage, perPage])

  // Combined fetch function
  const fetchData = useCallback(async () => {
    if (!role?.id) {
      setError("No role specified")
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Fetch both assigned menus and all available menus
      const [menusResponse, roleMenusResponse] = await Promise.all([
        MenuService.getAllUnpaginated(),
        RoleMenuService.getByRole(role.id, { page: currentPage, limit: perPage }),
      ])

      console.log("Menus response:", menusResponse)
      console.log("Role menus response:", roleMenusResponse)

      setAllMenus(menusResponse.results || [])
      setAssignedMenus(roleMenusResponse.results || [])
      setTotalRows(roleMenusResponse.count || 0)
    } catch (error: any) {
      console.error("fetchData error:", error)
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to load menu data"
      setError(errorMessage)
      toast.error(errorMessage)

      // Set empty arrays to prevent further errors
      setAllMenus([])
      setAssignedMenus([])
      setTotalRows(0)
    } finally {
      setLoading(false)
    }
  }, [role, currentPage, perPage])

  // Effect to fetch data when component mounts or dependencies change
  useEffect(() => {
    console.log("useEffect triggered, role:", role)

    if (role?.id) {
      fetchData()
    } else {
      setError("No role specified")
      setLoading(false)
    }
  }, [role, fetchData])

  // Effect to refetch all menus when modal opens or search term changes
  useEffect(() => {
    if (isAddModalOpen) {
      fetchAllMenus()
    }
  }, [isAddModalOpen, fetchAllMenus])

  // Handle bulk menu assignment
  const handleBulkAssignMenus = async () => {
    if (!role?.id || selectedMenuIds.length === 0) return

    setSaving(true)
    try {
      // Check for already assigned menus
      const alreadyAssigned = selectedMenuIds.filter((menuId) => assignedMenus.some((m) => m.menu === menuId))

      if (alreadyAssigned.length > 0) {
        toast.warning(`${alreadyAssigned.length} menu(s) already assigned`)
        setSelectedMenuIds(selectedMenuIds.filter((id) => !alreadyAssigned.includes(id)))
        return
      }

      // Create assignments for each selected menu
      const promises = selectedMenuIds.map((menuId) => RoleMenuService.create({ role: role.id, menu: menuId }))

      await Promise.all(promises)

      toast.success(`${selectedMenuIds.length} menu(s) assigned successfully`)
      setCurrentPage(1)
      await fetchData()
      setIsAddModalOpen(false)
      setSelectedMenuIds([])
    } catch (error: any) {
      console.error("handleBulkAssignMenus error:", error)
      toast.error("Failed to assign menus")
    } finally {
      setSaving(false)
    }
  }

  // Handle single menu removal
  const handleRemoveMenu = async (menuId: number, menuTitle: string) => {
    if (!window.confirm(`Remove "${menuTitle}" from this role?`)) {
      return
    }

    try {
      await RoleMenuService.delete(menuId)
      toast.success("Menu removed successfully")

      // Adjust pagination if needed
      if (assignedMenus.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1)
      } else {
        await fetchData()
      }
    } catch (error: any) {
      console.error("handleRemoveMenu error:", error)
      toast.error("Failed to remove menu")
    }
  }

  // Handle pagination
  const handlePageChange = (page: number) => setCurrentPage(page)
  const handlePerRowsChange = (newPerPage: number) => {
    setPerPage(newPerPage)
    setCurrentPage(1)
  }

  // Get available menus for assignment - with safety checks
  const availableMenus = allMenus.filter(
    (menu) => menu && !assignedMenus.some((m) => m.menu === menu.id) && menu.is_active,
  )

  // Handle menu selection in modal
  const handleMenuSelection = (menuId: number, checked: boolean) => {
    if (checked) {
      setSelectedMenuIds((prev) => [...prev, menuId])
    } else {
      setSelectedMenuIds((prev) => prev.filter((id) => id !== menuId))
    }
  }

  // Filter assigned menus based on search - with safety checks
  const filteredAssignedMenus = assignedMenus.filter((menu) => {
    if (!menu) return false

    const titleMatch = menu.menu_title?.toLowerCase().includes(searchTerm.toLowerCase()) || false
    const urlMatch = menu.menu_url?.toLowerCase().includes(searchTerm.toLowerCase()) || false

    return titleMatch || urlMatch
  })

  // Handle menu search in modal
  const handleMenuSearch = (searchValue: string) => {
    setMenuSearchTerm(searchValue)
  }

  // Table columns with safety checks
  const columns: TableColumn<RoleMenu>[] = [
    {
      name: "Menu",
      selector: (row: RoleMenu) => row.menu_title || "",
      sortable: true,
      cell: (row: RoleMenu) => (
        <div>
          <div className="fw-medium">{row.menu_title || "Unknown Menu"}</div>
          {row.menu_url && <small className="text-muted">{row.menu_url}</small>}
        </div>
      ),
      width: "50%",
    },
    {
      name: "Type",
      selector: (row: RoleMenu) => row.menu_type || "",
      sortable: true,
      cell: (row: RoleMenu) => (
        <Badge color="primary" className="text-capitalize">
          {row.menu_type || "unknown"}
        </Badge>
      ),
      width: "20%",
    },
    {
      name: "Assigned",
      selector: (row: RoleMenu) => row.created_at,
      sortable: true,
      cell: (row: RoleMenu) => (
        <small>{row.created_at ? new Date(row.created_at).toLocaleDateString() : "Unknown"}</small>
      ),
      width: "20%",
    },
    {
      name: "Actions",
      cell: (row: RoleMenu) => (
        <Button
          color="danger"
          size="sm"
          onClick={() => handleRemoveMenu(row.id, row.menu_title || "Unknown Menu")}
          title="Remove menu"
        >
          <FaTrash size={12} />
        </Button>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "10%",
    },
  ]

  // Early return with proper error handling
  if (!role) {
    return (
      <div className="container-fluid">
        <Alert color="danger">
          <h4>No Role Specified</h4>
          <p>Please select a role to manage menus.</p>
          <Button color="secondary" onClick={() => navigate(-1)}>
            <FaArrowLeft className="me-2" /> Back to Roles
          </Button>
        </Alert>
      </div>
    )
  }

  return (
    <div className="container-fluid">
      {/* Header Actions */}
      <Row className="mb-3">
        <Col md="6">
          <Button color="secondary" onClick={() => navigate(-1)}>
            <FaArrowLeft className="me-2" /> Back to Roles
          </Button>
        </Col>
        <Col md="6" className="text-end">
          <Button color="success" className="me-2" onClick={fetchData} disabled={loading}>
            <FaSync className="me-2" /> Refresh
          </Button>
          <Button color="primary" onClick={() => setIsAddModalOpen(true)} disabled={availableMenus.length === 0}>
            <FaPlus className="me-2" /> Assign Menus
          </Button>
        </Col>
      </Row>

      {/* Error Display */}
      {error && (
        <Alert color="danger" className="mb-3">
          <strong>Error:</strong> {error}
          <Button color="outline-danger" size="sm" className="ms-2" onClick={fetchData}>
            Retry
          </Button>
        </Alert>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner color="primary" size="lg" />
          <div className="mt-3">Loading assigned menus...</div>
        </div>
      ) : (
        <Card>
          <CardHeader>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <CardTitle tag="h5" className="mb-1">
                  Menu Assignments for "{role.name}"
                </CardTitle>
                <small className="text-muted">Manage menus assigned to this role</small>
              </div>
              <div className="text-end">
                <Badge color="info" className="me-2">
                  {totalRows} assigned
                </Badge>
                {/* <Badge color="secondary">{availableMenus.length} available</Badge> */}
              </div>
            </div>
          </CardHeader>

          <CardBody>
            {/* Search */}
            <div className="mb-3">
              <div className="position-relative">
                <FaSearch className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
                <Input
                  type="text"
                  className="ps-5"
                  placeholder="Search assigned menus..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Data Table */}
            <DataTable
              columns={columns}
              data={filteredAssignedMenus}
              pagination
              paginationServer
              paginationTotalRows={totalRows}
              paginationPerPage={perPage}
              paginationDefaultPage={currentPage}
              paginationRowsPerPageOptions={[10, 25, 50]}
              onChangePage={handlePageChange}
              onChangeRowsPerPage={handlePerRowsChange}
              highlightOnHover
              striped
              noDataComponent={
                <div className="text-center py-4">
                  <div className="text-muted">No menus assigned to this role</div>
                  <Button
                    color="primary"
                    size="sm"
                    className="mt-2"
                    onClick={() => setIsAddModalOpen(true)}
                    disabled={availableMenus.length === 0}
                  >
                    Assign First Menu
                  </Button>
                </div>
              }
              dense
              responsive
            />
          </CardBody>
        </Card>
      )}

      {/* Bulk Assignment Modal */}
      <Modal isOpen={isAddModalOpen} toggle={() => setIsAddModalOpen(false)} size="lg">
        <ModalHeader toggle={() => setIsAddModalOpen(false)}>Assign Menus to "{role.name}"</ModalHeader>
        <ModalBody>
          {availableMenus.length === 0 ? (
            <Alert color="info">All available menus are already assigned to this role.</Alert>
          ) : (
            <>
              <div className="mb-3">
                <Label>Select menus to assign ({selectedMenuIds.length} selected):</Label>
              </div>

              {/* Search within modal */}
              <div className="mb-3">
                <div className="position-relative">
                  <FaSearch className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
                  <Input
                    type="text"
                    className="ps-5"
                    placeholder="Search available menus..."
                    value={menuSearchTerm}
                    onChange={(e) => handleMenuSearch(e.target.value)}
                  />
                </div>
              </div>

              <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                {availableMenus.map((menu) => (
                  <FormGroup check key={menu.id} className="mb-2">
                    <Input
                      type="checkbox"
                      id={`menu-${menu.id}`}
                      checked={selectedMenuIds.includes(menu.id)}
                      onChange={(e) => handleMenuSelection(menu.id, e.target.checked)}
                    />
                    <Label check htmlFor={`menu-${menu.id}`} className="ms-2">
                      <div>
                        <strong>{menu.title}</strong>
                        <Badge color="secondary" className="ms-2 small">
                          {menu.menu_type}
                        </Badge>
                      </div>
                      {menu.url && <small className="text-muted">{menu.url}</small>}
                    </Label>
                  </FormGroup>
                ))}
              </div>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setIsAddModalOpen(false)}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleBulkAssignMenus} disabled={selectedMenuIds.length === 0 || saving}>
            {saving ? <Spinner size="sm" className="me-2" /> : null}
            Assign {selectedMenuIds.length} Menu{selectedMenuIds.length !== 1 ? "s" : ""}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}
